import { useInfiniteQuery } from '@zg-rentals/react-query5';
import { RentalManagerAccountViewZramMultifamilyWebApiHandler_list } from '@zg-rentals/rental-manager-env';

import PropertiesConstants from '../../constants/PropertiesConstants';

import type { AccountViewListingsDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { FilterState } from '../../containers/ZRAMPropertiesPage';

export const transformFiltersToApiParams = (filters: FilterState) => ({
  includeActive:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
  includeInactive:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
  includeArchived: filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
  includeUnarchived: filters.subNavFilterType !== PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
  includeFreeClaimedListingsOnly:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED,
  includeFreeBonusListingsOnly:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS,
  includeAtRiskOnly: filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK,
  searchText: filters.searchText,
  limit: PropertiesConstants.PROPERTIES_LIMIT_DEFAULT,
  startKeyExclusive: PropertiesConstants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT,
});

// Custom hook that provides the same interface as the original hook
export const useGetPropertiesData = (filters: FilterState) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError, isFetching } = useInfiniteQuery<
    AccountViewListingsDto,
    Error,
    { pages: Array<AccountViewListingsDto> },
    Array<string | FilterState>,
    string
  >({
    queryKey: ['getProperties', filters],
    queryFn: ({ pageParam }) =>
      RentalManagerAccountViewZramMultifamilyWebApiHandler_list({
        ...transformFiltersToApiParams(filters),
        startKeyExclusive: pageParam,
      }),
    initialPageParam: '',
    getNextPageParam: (lastPage) => (lastPage.pager?.hasMore ? lastPage.pager.endKey : undefined),
    // @ts-expect-error - returning undefined as it would be during pending
    placeholderData: (previousData) => {
      if (!previousData?.pages?.[0]?.listingCount) {
        return;
      }

      return {
        pages: [
          {
            listingCount: previousData.pages[0].listingCount,
          },
        ],
        pageParams: previousData.pageParams,
      };
    },
  });

  const propertiesData = data?.pages.length
    ? {
        listings: data.pages.flatMap((page) => page.listings),
        listingCount: data.pages[0].listingCount,
        pager: data.pages[data.pages.length - 1].pager,
      }
    : {};

  return {
    data: propertiesData,
    isPending,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
